import React from "react";

import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Paper, Typography } from "@mui/material";

import useAuth from "@app/common/auth/useAuth";
import useRecaptcha from "@app/common/auth/useRecaptcha";
import { Link } from "@app/common/components/Link";
import ROUTES from "@app/common/routes";
import Config from "@app/config";
import { yupResolver } from "@app/utils/forms/yupResolver";
import { getStringType } from "@app/utils/utils";

import Stack, { Row } from "@rui/Stack";

import { CenteredContainer } from "./styles";
import { requiredWhenEmail } from "./utils";
import TextField from "../components/TextField";

const signupValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email")
    .when("action", requiredWhenEmail),
});

const ForgotPassword = () => {
  const auth = useAuth();

  const { recaptchaRef, getRecaptchaValue } = useRecaptcha();

  const onSubmit = async (data: any) => {
    const recaptchaValue = await getRecaptchaValue();
    auth.requestPasswordReset(data.email, recaptchaValue);
  };
  const formResolver = yupResolver(signupValidationSchema);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ resolver: formResolver });

  return (
    <Stack sx={{ background: (theme) => theme.palette.background.emblem }}>
      <CenteredContainer>
        <Box sx={{ mb: "28px", width: { md: 518 } }}>
          <Paper elevation={6} sx={{ overflow: "hidden" }}>
            <Box sx={{ padding: "28px 56px 10px 56px", margin: "auto" }}>
              <Stack spacing={2}>
                <Typography variant="h2" sx={{ textAlign: "center" }}>
                  Forgot your password?
                </Typography>
                <Typography
                  variant="bodySecondaryReg"
                  sx={{ textAlign: "left" }}
                >
                  It happens. Enter the email address associated with your
                  account and we'll send you a link to reset your password.
                </Typography>
              </Stack>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={1} sx={{ padding: "25px 56px" }}>
                <TextField
                  data-test-id="auth-email-input-forgot-password"
                  label="Email"
                  InputProps={{ ...register("email") }}
                  error={!!errors.email}
                  helperText={getStringType(errors.email?.message)}
                />
              </Stack>
              <Stack sx={{ padding: "0px 56px 32px" }}>
                <LoadingButton
                  data-test-id="auth-submit-forgot-password"
                  variant="contained"
                  sx={{ textTransform: "none", padding: "10px 0" }}
                  onClick={() => setValue("action", "email")}
                  type="submit"
                  loading={auth.isLoading}
                >
                  Email me a recovery link
                </LoadingButton>
              </Stack>
              <Row variant="center" sx={{ mb: "20px" }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={Config.recaptchaSite}
                  size="invisible"
                  badge="inline"
                />
              </Row>
              <Box
                sx={{
                  padding: "0px 14px 30px 14px",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="bodySecondaryReg"
                  color="shades.neutral.300"
                >
                  Remember your password?&nbsp;
                  <Link to={ROUTES.SIGN_IN}>Sign in</Link>.
                </Typography>
              </Box>
            </form>
          </Paper>
        </Box>
        <Box sx={{ mb: "20px" }}>
          <Typography variant="bodySecondaryReg" sx={{ color: "white" }}>
            Don't have an account?&nbsp;
            <Link to={ROUTES.SIGN_UP} color="#FFF">
              Sign up
            </Link>
            .
          </Typography>
        </Box>
      </CenteredContainer>
    </Stack>
  );
};

export default ForgotPassword;
